import * as XLSX from 'xlsx';

const TARGET_SHEET = 'Catálogo de ventas';

// Normalize a cell header for fuzzy matching
const norm = (s) => String(s ?? '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Parse a price value that may come as a number or a formatted string like "$ 45,000"
function parsePrice(raw) {
  if (raw === null || raw === undefined || raw === '') return NaN;
  if (typeof raw === 'number') return raw;
  // Remove currency symbols, spaces, dots used as thousands separators
  const cleaned = String(raw).replace(/[$\s]/g, '').replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned);
}

function parseStock(raw) {
  if (raw === null || raw === undefined || raw === '') return 0;
  if (typeof raw === 'number') return Math.round(raw);
  const n = parseInt(String(raw).replace(/[^0-9]/g, ''), 10);
  return isNaN(n) ? 0 : n;
}

/**
 * Parse a Beauty Hunters .xlsx file.
 * Returns { products: [...], skippedRows: [...] }
 *
 * Each product: { nombre, marca, variaciones: [{ tono, precio, stock }] }
 */
export function parseBeautyHunters(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });

  const ws = wb.Sheets[TARGET_SHEET];
  if (!ws) {
    const available = wb.SheetNames.join(', ');
    throw new Error(
      `No se encontró la hoja "${TARGET_SHEET}". Hojas disponibles: ${available}`
    );
  }

  // Convert to array-of-arrays; defval keeps empty cells as ''
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

  // ── Detect header row ──────────────────────────────────────────────────────
  let headerIdx = -1;
  const colIdx = {};

  for (let i = 0; i < Math.min(15, rows.length); i++) {
    const row = rows[i];
    const hasMarca = row.some((c) => norm(c) === 'marca');
    if (!hasMarca) continue;

    headerIdx = i;
    row.forEach((cell, j) => {
      const n = norm(cell);
      if (n === 'marca')                        colIdx.marca    = j;
      else if (n === 'producto')                colIdx.producto = j;
      else if (n === 'categoria' || n === 'categoría') colIdx.cat = j;
      else if (n.includes('tono') || n.includes('tamano') || n.includes('tamaño'))
                                                colIdx.tono     = j;
      else if (n.includes('precio') && n.includes('venta'))
                                                colIdx.precio   = j;
      else if (n === 'unidades')                colIdx.stock    = j;
    });
    break;
  }

  if (headerIdx === -1) {
    throw new Error(
      'No se encontró la fila de encabezados. Verifica que la hoja tenga las columnas Marca, Producto, Precio venta, Unidades.'
    );
  }

  const missing = ['marca', 'producto', 'tono', 'precio', 'stock'].filter((k) => colIdx[k] === undefined);
  if (missing.length) {
    throw new Error(`Faltan columnas requeridas: ${missing.join(', ')}`);
  }

  // ── Parse data rows ────────────────────────────────────────────────────────
  const dataRows = rows.slice(headerIdx + 1);

  // productMap key → product object (preserves insertion order)
  const productMap = new Map();
  const skippedRows = [];

  let lastMarca    = '';
  let lastProducto = '';

  for (let i = 0; i < dataRows.length; i++) {
    const row    = dataRows[i];
    const rowNum = headerIdx + 2 + i; // 1-based Excel row number

    const rawMarca    = String(row[colIdx.marca]    ?? '').trim();
    const rawProducto = String(row[colIdx.producto] ?? '').trim();
    const rawTono     = String(row[colIdx.tono]     ?? '').trim();
    const rawPrecio   = row[colIdx.precio];
    const rawStock    = row[colIdx.stock];

    // Stop sentinel: row whose first non-empty text starts with "total"
    const firstText = row.find((c) => String(c).trim() !== '');
    if (firstText && norm(firstText).startsWith('total')) break;

    // Forward-fill
    if (rawMarca)    lastMarca    = rawMarca;
    if (rawProducto) lastProducto = rawProducto;

    // Skip rows with no product context
    if (!lastMarca && !lastProducto) {
      skippedRows.push({ row: rowNum, reason: 'Sin marca ni producto (fila vacía o encabezado)' });
      continue;
    }

    // Validate price
    const precio = parsePrice(rawPrecio);
    if (isNaN(precio) || precio <= 0) {
      skippedRows.push({ row: rowNum, reason: `Sin precio de venta válido (valor: "${rawPrecio}")` });
      continue;
    }

    const nombre = `${lastMarca} - ${lastProducto}`;
    const key    = `${lastMarca}|||${lastProducto}`;

    if (!productMap.has(key)) {
      productMap.set(key, {
        nombre,
        marca:      lastMarca,
        variaciones: [],
      });
    }

    productMap.get(key).variaciones.push({
      tono:   rawTono || 'Única',
      precio: String(Math.round(precio)),
      stock:  parseStock(rawStock),
    });
  }

  return {
    products:    [...productMap.values()],
    skippedRows,
  };
}
